import { supabase } from '@/integrations/supabase/client';
import type { PEOProvider } from '@/types/peo.types';
import type { Application } from '@/types/database.types';
import type { MatchResult } from '@/types/pinecone.types';
import { 
  indexConfig, 
  getIndex,
  createPineconeIndex, 
  deletePineconeIndex, 
  listPineconeIndexes 
} from '@/integrations/pinecone/client';

// The name of the index used for PEO providers
export const PEO_INDEX_NAME = 'peo-providers';

/**
 * Initialize the Pinecone index for PEO providers if it doesn't exist
 */
export const initializePineconeIndex = async () => {
  try {
    const { success, indexes, message } = await listPineconeIndexes();
    
    if (!success) {
      throw new Error(message);
    }
    
    // Handle both array and object responses from Pinecone API
    const indexExists = Array.isArray(indexes) 
      ? indexes.some(index => index.name === PEO_INDEX_NAME)
      : indexes.indexes?.some(index => index.name === PEO_INDEX_NAME) || false;
    
    if (!indexExists) {
      return createPineconeIndex(
        PEO_INDEX_NAME,
        1536, // Dimension for OpenAI's text-embedding-3-small
        'cosine',
        'aws',
        'us-east-1'
      );
    }
    
    return { success: true, message: `Index "${PEO_INDEX_NAME}" already exists` };
  } catch (error) {
    console.error('Error initializing Pinecone index:', error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Unknown error initializing index' 
    };
  }
};

/**
 * Process all PEO providers and create embeddings in Pinecone
 */
export const processAllProviders = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    // First ensure the index exists
    const initResult = await initializePineconeIndex();
    if (!initResult.success) {
      console.warn('Warning during index initialization:', initResult.message);
      // Continue anyway as the index might exist but had an error in creation
    }
    
    const { data, error } = await supabase.functions.invoke('generate-embeddings', {
      body: {},
      method: 'POST'
    });

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error processing providers:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Find the best matching PEO providers for an application
 */
export const findMatchingProviders = async (
  applicationId: string
): Promise<{ success: boolean; matches?: MatchResult[]; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('generate-embeddings', {
      body: { applicationId },
      method: 'POST'
    });

    if (error) throw error;
    
    return { 
      success: true, 
      matches: data.matches 
    };
  } catch (error: any) {
    console.error('Error finding matching providers:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate a human-readable match percentage from a match score
 */
export const getMatchPercentage = (score: number): number => {
  // Convert the score to a percentage (0-100)
  return Math.round(score * 100);
};

/**
 * Get a description of why a provider matches an application
 */
export const getMatchReasoning = (match: MatchResult): string[] => {
  const reasons: string[] = [];
  const details = match.matchDetails;
  const provider = match.provider;
  
  // Size match
  if (details.sizeMatch > 0.8) {
    reasons.push(`Ideal employee count for this provider`);
  }
  
  // Location match
  if (details.locationMatch > 0.8) {
    reasons.push(`Serves your state`);
  }
  
  // Industry match
  if (details.industryMatch > 0.8) {
    reasons.push(`Experience with your industry`);
  }
  
  // Needs match - more specific reasons
  if (provider.international_capabilities) {
    reasons.push(`Offers international capabilities`);
  }
  
  if (provider.startup_friendly) {
    reasons.push(`Works well with startups`);
  }
  
  if (provider.nonprofit_experience) {
    reasons.push(`Experienced with nonprofits`);
  }
  
  // Add certification reasons
  if (provider.is_esac_accredited) {
    reasons.push(`ESAC accredited`);
  }
  
  // If we don't have many reasons, add a generic one
  if (reasons.length < 2) {
    reasons.push(`Overall good match for your requirements`);
  }
  
  return reasons;
};
